import {
  TieredPackage,
  PricingContextState,
  PricingTier,
  NationalityType,
  Season,
  TierVariant,
  PricingBreakdown,
  FinalPrice
} from '../types';
import { seasonService } from './seasonService';
import { currencyService } from './currencyService';
import { transportService } from './transportService';
import { guideService } from './guideService';
import { accommodationPoolService } from './accommodationPoolService';



class PricingService {
  /**
   * Authoritative pricing engine
   * Pipeline: Resolve Season -> Calculate Costs -> Apply Multipliers -> Convert Currency
   */
  async calculatePackagePrice(
    pkg: any,
    context: PricingContextState
  ): Promise<FinalPrice> {
    const USE_BACKEND_API = process.env.REACT_APP_USE_BACKEND_API === 'true';

    if (USE_BACKEND_API) {
      const { apiService } = await import('./apiService');
      return await apiService.calculatePrice(pkg, context);
    }

    // 2. Resolve Tier Pricing with Discount Fallback
    const tier = context.tier || PricingTier.STANDARD;
    // When useOriginalPricing=true, we skip discountedTierVariants to get the WAS price
    const discountedData = !context.useOriginalPricing && pkg.discountedTierVariants?.[tier];
    let tierData = discountedData || pkg.tierVariants?.[tier];

    // If discount exists, we should flag it so UI can show "WAS/NOW"
    const isDiscounted = !!pkg.discountedTierVariants?.[tier];

    if (!tierData) {
      // Robust Fallback: Try to find ANY available tier
      const availableTiers = Object.values(PricingTier).filter(t => !!pkg.tierVariants?.[t]);
      if (availableTiers.length > 0) {
        tierData = pkg.tierVariants[availableTiers[0]];
      } else {
        throw new Error(`Pricing not configured for tier: ${tier}`);
      }
    }

    // 1. Resolve Season
    let season: Season;
    const dynamicSeasons = pkg.seasonalAdjustments || pkg.seasonal_adjustments;

    if (context.travelDates) {
      if (Array.isArray(dynamicSeasons) && dynamicSeasons.length > 0) {
        // Simple client-side resolver for custom seasons
        const date = new Date(context.travelDates.startDate);
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const dateScore = month * 100 + day;

        const matching = dynamicSeasons.filter((s: Season) => {
          const startScore = s.startMonth * 100 + (s.startDay || 1);
          const endScore = s.endMonth * 100 + (s.endDay || 31);

          if (startScore <= endScore) {
            return dateScore >= startScore && dateScore <= endScore;
          } else {
            return dateScore >= startScore || dateScore <= endScore;
          }
        });
        season = matching.length > 0
          ? matching.reduce((prev: Season, curr: Season) => curr.priceMultiplier > prev.priceMultiplier ? curr : prev)
          : { id: 'default', name: 'High', startMonth: 1, startDay: 1, endMonth: 12, endDay: 31, priceMultiplier: 1.0 };
      } else {
        season = seasonService.resolveSeasonForRange(
          context.travelDates.startDate,
          context.travelDates.endDate
        );
      }
    } else {
      // Default fallback (High Season, 1.0 multiplier)
      season = { id: 'default', name: 'High', startMonth: 1, startDay: 1, endMonth: 12, endDay: 31, priceMultiplier: 1.0 };
    }

    // 1. Resolve Season ... (existing season logic)
    // ...

    // 2. Accommodation Cost (Highest Rate Rule)
    const duration = pkg.durationDays || 1;
    const accommodationCost = await this.calculateAccommodationCost(tierData, season, context.pax, duration);

    // 3. Logistics & Guide Dynamic Pricing
    let dynamicLogisticsCost = 0;
    if (tierData.transportId && tierData.transportId !== 'legacy') {
        const transport = await transportService.getTransportOptionById(tierData.transportId);
        if (transport) dynamicLogisticsCost += transport.basePricePerDay * duration;
    }

    if (tierData.guideRankingId && tierData.guideRankingId !== 'non-guided') {
        const ranking = await guideService.getGuideRankingById(tierData.guideRankingId);
        if (ranking) dynamicLogisticsCost += ranking.dailyRate * duration;
    }

    // 4. Base Service Costs (Per Nationality)
    const baseCosts = tierData.baseServiceCosts[context.nationalityType] || { adult: 0, child: 0 };
    const serviceCost = (baseCosts.adult * context.pax.adults) + (baseCosts.child * context.pax.children) + dynamicLogisticsCost;

    // Determine Source and Target Currencies early
    const sourceCurrency = context.nationalityType === NationalityType.LOCAL ? 'KES' : 'USD';
    let targetCurrency = context.displayCurrency;

    // Fallback if no target currency
    if (!targetCurrency) {
      targetCurrency = sourceCurrency;
    }

    // 4. Normalize Accommodation Cost (Assumed USD in DB) to Source Currency
    // If Source is KES, we must convert Accom(USD) -> Accom(KES) before adding to Service(KES)
    let normalizedAccommodationCost = accommodationCost;
    if (sourceCurrency !== 'USD') {
      normalizedAccommodationCost = await currencyService.convert(accommodationCost, 'USD', sourceCurrency);
    }

    // 5. Calculate Subtotal (now in uniform Source Currency)
    const subtotal = normalizedAccommodationCost + serviceCost;

    // 6. Apply Season Multiplier
    const totalInSource = subtotal * season.priceMultiplier;

    // 7. Convert to Target Currency (if different from Source)
    // Note: If Source was KES and Target is KES, convert returns 1:1
    const convertedTotal = await currencyService.convert(totalInSource, sourceCurrency, targetCurrency);
    const convertedSubtotal = await currencyService.convert(subtotal, sourceCurrency, targetCurrency);
    // accommodationCost is USD, convert directly to target
    const convertedAcc = await currencyService.convert(accommodationCost, 'USD', targetCurrency);
    const convertedService = await currencyService.convert(serviceCost, sourceCurrency, targetCurrency);

    // 8. Breakdown
    const breakdown: PricingBreakdown[] = [
      {
        item: 'Accommodation Pool',
        description: `Selected ${context.tier} accommodation for travel window`,
        quantity: 1,
        unitPrice: convertedAcc, // Already converted to target
        total: convertedAcc,
        type: 'accommodation'
      },
      {
        item: 'Base Logistics & Services',
        description: `Tier-specific transport, guide, and fees for ${context.nationalityType} clients`,
        quantity: 1,
        unitPrice: convertedService,
        total: convertedService,
        type: 'tour'
      }
    ];

    if (season.priceMultiplier !== 1) {
      const adjustment = convertedTotal - (convertedAcc + convertedService);
      breakdown.push({
        item: `Seasonal Adjustment (${season.name})`,
        description: `Multiplier of ${season.priceMultiplier}x applied to base costs`,
        quantity: 1,
        unitPrice: adjustment,
        total: adjustment,
        type: 'tax' // Representing adjustment
      });
    }

    // 9. Handle Extras
    let extrasTotal = 0;
    const selectedExtras = context.selectedExtras || [];

    for (const extra of selectedExtras) {
      let extraBaseCost = 0;
      if (extra.unit === 'per person') {
        extraBaseCost = extra.price * (context.pax.adults + context.pax.children);
      } else {
        extraBaseCost = extra.price;
      }

      const convertedExtraCost = await currencyService.convert(extraBaseCost, 'USD', targetCurrency);
      extrasTotal += convertedExtraCost;

      breakdown.push({
        item: extra.name,
        description: `Extra: ${extra.unit}`,
        quantity: 1,
        unitPrice: convertedExtraCost,
        total: convertedExtraCost,
        type: 'extra'
      });
    }

    const finalTotal = Math.ceil(convertedTotal + extrasTotal);

    // Calculate original total if this was a discounted price
    let originalTotal: number | undefined;
    if (isDiscounted && !context.useOriginalPricing) {
      const originalContext = { ...context, useOriginalPricing: true };
      const originalResult = await this.calculatePackagePrice(pkg, originalContext);
      originalTotal = originalResult.total;
    }

    return {
      total: finalTotal,
      subtotal: Math.ceil(convertedSubtotal),
      accommodationCost: Math.ceil(convertedAcc),
      serviceCost: Math.ceil(convertedService),
      seasonMultiplier: season.priceMultiplier,
      currency: targetCurrency,
      breakdown,
      originalTotal,
      isDiscounted
    };
  }

  /**
   * Synchronous pricing calculation (fallback for instant UI)
   */
  calculatePackagePriceSync(
    pkg: any,
    context: PricingContextState
  ): FinalPrice | null {
    try {
      const tier = context.tier || PricingTier.STANDARD;
      const discountedData = !context.useOriginalPricing && pkg.discountedTierVariants?.[tier];
      let tierData = discountedData || pkg.tierVariants?.[tier];

      if (!tierData) {
        const availableTiers = Object.values(PricingTier).filter(t => !!pkg.tierVariants?.[t]);
        if (availableTiers.length > 0) tierData = pkg.tierVariants[availableTiers[0]];
        else return null;
      }

      const season: Season = { id: 'default', name: 'High', startMonth: 1, startDay: 1, endMonth: 12, endDay: 31, priceMultiplier: 1.0 };
      const duration = pkg.durationDays || 1;
      // Sync fallback doesn't support async dynamic loading - returns base service + legacy pools
      const accommodationCost = 0; // Simplified for sync
      const baseCosts = tierData.baseServiceCosts[context.nationalityType] || { adult: 0, child: 0 };
      const serviceCost = (baseCosts.adult * context.pax.adults) + (baseCosts.child * context.pax.children);

      const sourceCurrency = context.nationalityType === NationalityType.LOCAL ? 'KES' : 'USD';
      const targetCurrency = context.displayCurrency || sourceCurrency;

      let normalizedAccommodationCost = accommodationCost;
      if (sourceCurrency !== 'USD') {
        const converted = currencyService.convertSync(accommodationCost, 'USD', sourceCurrency);
        if (converted === null) return null;
        normalizedAccommodationCost = converted;
      }

      const subtotal = normalizedAccommodationCost + serviceCost;
      const totalInSource = subtotal * season.priceMultiplier;

      const convertedTotal = currencyService.convertSync(totalInSource, sourceCurrency, targetCurrency);
      const convertedSubtotal = currencyService.convertSync(subtotal, sourceCurrency, targetCurrency);
      const convertedAcc = currencyService.convertSync(accommodationCost, 'USD', targetCurrency);
      const convertedService = currencyService.convertSync(serviceCost, sourceCurrency, targetCurrency);

      if (convertedTotal === null || convertedSubtotal === null || convertedAcc === null || convertedService === null) return null;

      return {
        total: Math.ceil(convertedTotal),
        subtotal: Math.ceil(convertedSubtotal),
        accommodationCost: Math.ceil(convertedAcc),
        serviceCost: Math.ceil(convertedService),
        seasonMultiplier: season.priceMultiplier,
        currency: targetCurrency,
        breakdown: [],
        isDiscounted: !!pkg.discountedTierVariants?.[tier]
      };
    } catch {
      return null;
    }
  }

  /**
   * Risk-Safe Accommodation Calculation
   * Heuristic: Picks the most expensive hotel in the pool to ensure profit margin
   */
  private async calculateAccommodationCost(tierData: TierVariant, season: Season, pax: { adults: number; children: number }, duration: number): Promise<number> {
    // Try dynamic pool first
    if (tierData.accommodationPoolId) {
        try {
            const pool = await accommodationPoolService.getAccommodationPoolById(tierData.accommodationPoolId);
            if (pool) {
                return pool.pricePerNight * (pax.adults + pax.children) * duration;
            }
        } catch (err) {
            console.warn('Dynamic pool fetch failed, falling back to legacy', err);
        }
    }

    let total = 0;
    if (!tierData.accommodationPools) return 0;

    for (const pool of tierData.accommodationPools) {
      let highestAdultRate = 0;
      let highestChildRate = 0;

      for (const hotel of pool.hotels) {
        // Find rate for resolved season
        const rate = hotel.seasonalRates.find(r => r.seasonId === season.id);

        // Fallback or exact match
        const finalRate = rate || hotel.seasonalRates[0]; // Fallback to first if none

        if (finalRate) {
          highestAdultRate = Math.max(highestAdultRate, finalRate.adultRate);
          highestChildRate = Math.max(highestChildRate, finalRate.childRate);
        }
      }

      // Add to running total
      total += (highestAdultRate * pax.adults) + (highestChildRate * pax.children);
    }

    // Heuristic: Legacy pools were often per-person per-stay or per-night. 
    // If duration > 1 and total seems low, we might need * duration. 
    // But for safety with existing data, we'll keep legacy as is unless explicitly migrating.
    return total;
  }
}

export const pricingService = new PricingService();
export default pricingService;
// cache bust
